// Login and sessions of the app core (AD-32, AD-34): framework-free, every answer checked against app-v1. The session
// token lives only in the HttpOnly cookie; the app sees the user, the session's expiry and whether it is fresh.
import {
  validate,
  type AuthProvidersResponse,
  type AuthSessionResponse,
  type EmailCodeStartResponse,
  type HttpError,
  type MessageSchemaName,
  type SessionListItem,
} from '@brewcore/app-contract';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type AuthResult<T> =
  { ok: true; value: T } | { ok: false; error: string; retryAfter?: number; attemptsLeft?: number };

/** Removes spaces and dashes the user typed ("1234 5678"). */
export const normalizeCode = (code: string): string => code.replace(/[\s-]/g, '');

/** Only app-internal paths are allowed as return targets (no open redirects). */
export function safeNext(next: string | null | undefined): string {
  return next && /^\/(?![/\\])[^\s]*$/.test(next) && next.length <= 200 ? next : '/';
}

export interface AuthClientOptions {
  fetch?: FetchLike;
  /** Session check again when the app comes back after this long (sliding expiry, FR-2). */
  refreshEveryMs?: number;
}

export class AuthClient {
  private readonly fetchFn: FetchLike;
  private lastCheck = 0;
  private readonly refreshEveryMs: number;

  constructor(opts: AuthClientOptions = {}) {
    this.fetchFn = opts.fetch ?? ((input, init) => fetch(input, init));
    this.refreshEveryMs = opts.refreshEveryMs ?? 3_600_000;
  }

  private async call<T>(
    method: string,
    path: string,
    body: unknown,
    schema: MessageSchemaName | null,
  ): Promise<AuthResult<T>> {
    let res: Response;
    try {
      res = await this.fetchFn(path, {
        method,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: body === undefined ? {} : { 'content-type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      return { ok: false, error: 'network' };
    }
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      const err = validate('http-error', json);
      if (!err.ok) return { ok: false, error: res.status === 401 ? 'unauthorized' : 'auth_failed' };
      const e: HttpError = err.value;
      return {
        ok: false,
        error: e.code,
        ...(e.retryAfter !== undefined ? { retryAfter: e.retryAfter } : {}),
        ...(e.attemptsLeft !== undefined ? { attemptsLeft: e.attemptsLeft } : {}),
      };
    }
    // schema-less answers: 204 (no body) or the pass-through WebAuthn JSON of the passkey plugin
    if (!schema) return { ok: true, value: json as T };
    const parsed = validate(schema, json);
    return parsed.ok ? { ok: true, value: parsed.value as T } : { ok: false, error: 'malformed' };
  }

  /** The current session (and a renewed cookie when due), or null when not signed in. */
  async session(): Promise<AuthResult<AuthSessionResponse | null>> {
    const r = await this.call<AuthSessionResponse>(
      'GET',
      '/api/auth/session',
      undefined,
      'http-auth-session',
    );
    this.lastCheck = Date.now();
    if (!r.ok && r.error === 'unauthorized') return { ok: true, value: null };
    return r;
  }

  /** Optional sign-in methods of the deployment (public). */
  providers(): Promise<AuthResult<AuthProvidersResponse>> {
    return this.call('GET', '/api/auth/providers', undefined, 'http-auth-providers');
  }

  /** Back in the foreground: check the session again once an hour (renews the sliding expiry). */
  dueForRefresh(now = Date.now()): boolean {
    return now - this.lastCheck >= this.refreshEveryMs;
  }

  startEmail(email: string, lang: 'de' | 'en'): Promise<AuthResult<EmailCodeStartResponse>> {
    return this.call(
      'POST',
      '/api/auth/email-code/start',
      { email: email.trim(), lang },
      'http-email-code-start-response',
    );
  }

  verifyEmail(email: string, code: string): Promise<AuthResult<AuthSessionResponse>> {
    return this.call(
      'POST',
      '/api/auth/email-code/verify',
      { email: email.trim(), code: normalizeCode(code) },
      'http-auth-session',
    );
  }

  /** Google login (or linking, with a fresh session): the caller navigates to the returned URL. */
  startGoogle(next: string, link = false): Promise<AuthResult<{ url: string }>> {
    return this.call(
      'POST',
      '/api/auth/google/start',
      { next: safeNext(next), ...(link ? { link: true } : {}) },
      'http-google-start-response',
    );
  }

  logout(): Promise<AuthResult<void>> {
    return this.call('POST', '/api/auth/logout', undefined, null);
  }

  logoutEverywhere(): Promise<AuthResult<void>> {
    return this.call('POST', '/api/auth/logout-all', undefined, null);
  }

  async sessions(): Promise<AuthResult<SessionListItem[]>> {
    const r = await this.call<{ sessions: SessionListItem[] }>(
      'GET',
      '/api/auth/sessions',
      undefined,
      'http-sessions',
    );
    return r.ok ? { ok: true, value: r.value.sessions } : r;
  }

  endSession(id: string): Promise<AuthResult<void>> {
    return this.call('DELETE', `/api/auth/sessions/${encodeURIComponent(id)}`, undefined, null);
  }

  /** Passkey ceremonies (operator): WebAuthn options from the API, the ceremony in the browser. */
  async passkeyRegisterOptions(): Promise<AuthResult<unknown>> {
    return this.raw('GET', '/api/auth/passkey/generate-register-options');
  }

  async passkeyRegister(response: unknown, name: string): Promise<AuthResult<unknown>> {
    return this.raw('POST', '/api/auth/passkey/verify-registration', { response, name });
  }

  async passkeyLoginOptions(): Promise<AuthResult<unknown>> {
    return this.raw('GET', '/api/auth/passkey/generate-authenticate-options');
  }

  async passkeyLogin(response: unknown): Promise<AuthResult<unknown>> {
    return this.raw('POST', '/api/auth/passkey/verify-authentication', { response });
  }

  /** Pass-through endpoints (WebAuthn JSON, not described in app-v1 beyond the error form). */
  private raw(method: string, path: string, body?: unknown): Promise<AuthResult<unknown>> {
    return this.call<unknown>(method, path, body, null);
  }
}
